import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CircleArrowUp } from "lucide-react";
import { TrackModal } from "../components/TrackModal";
import "../styles/AudioTrack.css";
import { TrackData } from "../types/Types";
import { AudioTrackService } from "../services/AudioTrackService";
import { useNotification } from "../context/NotificationContext";
import * as amplitude from "@amplitude/analytics-browser";

interface AudioTrackProps {
  track: TrackData;
  rank?: number;
}

export const AudioTrack = ({ track, rank }: AudioTrackProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const { showNotification } = useNotification();

  const getArtistSlug = () => {
    const rawInput = track.userName || track.artistName || "unknown";
    return rawInput.replace(/\s+/g, "");
  };

  const handleRowClick = () => {
    amplitude.track("track_tapped", {
      track_id: track.id,
      track_title: track.trackTitle,
      artist_name: track.artistName,
      genre: track.genreName,
      rank: rank,
    });

    setIsModalOpen(true);
  };

  const handleUpvote = async (e: React.MouseEvent) => {
    e.stopPropagation();

    amplitude.track("track_upvoted", {
      track_id: track.id,
      track_title: track.trackTitle,
      artist_name: track.artistName,
    });

    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    showNotification(`Upvoted "${track.trackTitle}"`);

    if (track.id) {
      await AudioTrackService.submitUpvote(track.id, "tracks");
    }
  };

  return (
    <>
      <div
        className="audio-track-row"
        onClick={handleRowClick}
        role="button"
        tabIndex={0}
      >
        {rank !== undefined && <div className="audio-track-rank">{rank}</div>}

        <img
          src={track.coverArtUrl || "https://via.placeholder.com/50"}
          alt={track.trackTitle}
          className="audio-track-cover"
        />

        <div className="audio-track-info">
          <span className="audio-track-title">{track.trackTitle}</span>
          <Link
            to={`/artist/${getArtistSlug()}`}
            className="audio-track-artist-link"
            onClick={(e) => e.stopPropagation()}
          >
            {track.artistName}
          </Link>

          {track.genreName && (
            <span className="audio-track-genre">{track.genreName}</span>
          )}
        </div>

        <div className="audio-track-actions">
          <button
            className={`audio-track-btn ${isAnimating ? "pop-animation" : ""}`}
            onClick={handleUpvote}
            title="Upvote this track"
          >
            <CircleArrowUp
              size={18}
              color={isAnimating ? "#4CAF50" : "currentColor"}
            />
          </button>
        </div>
      </div>

      <TrackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        track={track}
      />
    </>
  );
};
